import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "../../App"; // Import from App.js
import { 
  Home, Bell, BellDot, ShoppingBag, Settings, 
  Users, ShoppingCart, Mail, AlertCircle, 
  CheckCircle, CreditCard, Calendar, Share2, Loader2,
  Mail as MailIcon, Smartphone, Save, RefreshCw,
  X, ExternalLink, Shield, Battery, Wifi as WifiIcon,
  Play, Zap
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import DashboardHeader from "../../components/app_components/DashboardHeader";
import MobileBottomNav from "../../components/app_components/mobile/MobileBottomNav";
import { useTranslation } from '../../contexts/LanguageContext';

const MobileNotifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  
  const [activeTab, setActiveTab] = useState('all');
  const [notificationSettings, setNotificationSettings] = useState({
    global_settings: {
      ShareSubs: { email: true, inApp: true, push: true },
      Security: { email: true, inApp: true, push: true },
      Promotion: { email: true, inApp: true, push: false },
      FreeTrialAlert: { email: true, inApp: true, push: true },
      PriceRadarAlert: { email: true, inApp: true, push: true }
    },
    merchant_promo_settings: {}
  });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [animatingNotifications, setAnimatingNotifications] = useState(new Set());
  const [animationStage, setAnimationStage] = useState('idle');
  const [permissionRequested, setPermissionRequested] = useState(false);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [pushPermissionStatus, setPushPermissionStatus] = useState('unknown');
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  const [sendingPushTest, setSendingPushTest] = useState(false);
  const [pushToken, setPushToken] = useState(null);
  const [hasPushSupport, setHasPushSupport] = useState(false);
  const [hasLocalNotificationSupport, setHasLocalNotificationSupport] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [tokenRegistrationStatus, setTokenRegistrationStatus] = useState('idle');
  const [showOSGuide, setShowOSGuide] = useState(false);
  const [currentOS, setCurrentOS] = useState('');
  
  // Add premium status state
  const [isPremium, setIsPremium] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const isMounted = useRef(true);

  // =============================================
  // 🔄 INITIALIZATION
  // =============================================
  useEffect(() => {
    try {
      const platform = Capacitor.getPlatform();
      setIsNativePlatform(Capacitor.isNativePlatform());
      setHasPushSupport(typeof PushNotifications !== 'undefined' && Capacitor.isNativePlatform());
      setHasLocalNotificationSupport(typeof LocalNotifications !== 'undefined');
      
      // Set device info
      setDeviceInfo({
        platform: platform,
        appVersion: '1.0.0',
        deviceId: generateDeviceId(),
        isNative: Capacitor.isNativePlatform()
      });

      // Set current OS for guide
      setCurrentOS(platform === 'ios' ? 'ios' : 'android');

      // Check for existing token in localStorage
      const storedToken = localStorage.getItem('pushToken');
      if (storedToken) {
        setPushToken(storedToken);
        setTokenRegistrationStatus('registered');
      }
    } catch (err) {
      console.error('Error during initialization:', err);
      setIsNativePlatform(false);
      setHasPushSupport(false);
      setHasLocalNotificationSupport(false);
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  // =============================================
  // 🚀 AUTO MARK ALL AS READ
  // =============================================
  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchNotificationSettings();
      fetchPremiumStatus(); // Fetch premium status
    } else {
      navigate("/demo");
    }
  }, [user, navigate]);

  // Mark all notifications as read when notifications are loaded
  useEffect(() => {
    if (!loading && notifications.length > 0 && unreadCount > 0) {
      smoothAnimateMarkAllAsRead();
    }
  }, [loading, notifications]);

  // Fetch premium status
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };

  // Generate a simple device ID
  const generateDeviceId = () => {
    try {
      let deviceId = localStorage.getItem('deviceId');
      if (!deviceId) {
        deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('deviceId', deviceId);
      }
      return deviceId;
    } catch (error) {
      return `device_${Date.now()}`;
    }
  };

  // =============================================
  // 🔔 PUSH NOTIFICATION SETUP
  // =============================================
  useEffect(() => {
    if (!hasPushSupport || !user) {
      return;
    }

    const setupPushNotifications = async () => {
      try {
        // Remove any existing listeners first
        try {
          await PushNotifications.removeAllListeners();
        } catch (listenerError) {
          // Ignore error if no listeners to remove
        }

        // Add registration listener
        await PushNotifications.addListener('registration', async (token) => {
          setPushToken(token.value);
          localStorage.setItem('pushToken', token.value);
          localStorage.setItem('pushTokenReceivedAt', new Date().toISOString());
          setTokenRegistrationStatus('registered');
          
          // Store token on server
          await storePushToken(token.value);
        });

        // Add registration error listener
        await PushNotifications.addListener('registrationError', (error) => {
          setError('Push registration failed: ' + error.message);
          setTokenRegistrationStatus('error');
        });

        // Add notification received listener
        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          fetchNotifications();
        });

        // Add notification action listener
        await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          navigate('/mobile-notifications');
        });

        // Check current permission status
        const permStatus = await PushNotifications.checkPermissions();
        setPushPermissionStatus(permStatus.receive);

        // Auto-register if permission is granted
        if (permStatus.receive === 'granted') {
          const storedToken = localStorage.getItem('pushToken');
          if (storedToken) {
            setPushToken(storedToken);
            setTokenRegistrationStatus('registered');
          } else {
            try {
              await PushNotifications.register();
            } catch (registerError) {
              console.error('Error calling PushNotifications.register():', registerError);
            }
          }
        }

      } catch (error) {
        console.error('Error setting up push notifications:', error);
        setError('Failed to setup push notifications: ' + error.message);
      }
    };

    setupPushNotifications();
  }, [hasPushSupport, navigate, user]);

  // =============================================
  // 💾 STORE PUSH TOKEN TO BACKEND
  // =============================================
  const storePushToken = async (token) => {
    try {
      setTokenRegistrationStatus('registering');
      
      if (!httpClient || !user) {
        setTokenRegistrationStatus('error');
        return false;
      }

      const response = await httpClient.request('/api/register-push-token', {
        method: 'POST',
        body: JSON.stringify({
          token: token,
          platform: Capacitor.getPlatform(),
          device_id: deviceInfo?.deviceId,
          app_version: deviceInfo?.appVersion
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        setTokenRegistrationStatus('registered');
        return true;
      } else {
        throw new Error(result.error || `Server error: ${response.status}`);
      }
    } catch (err) {
      setError('Failed to register device for push notifications: ' + err.message);
      setTokenRegistrationStatus('error');
      return false;
    }
  };

  // =============================================
  // 📝 MARK ALL NOTIFICATIONS AS READ
  // =============================================
  const smoothAnimateMarkAllAsRead = async () => {
    try {
      setMarkingAllAsRead(true);
      setAnimationStage('fading');
      
      if (!httpClient) {
        setMarkingAllAsRead(false);
        setAnimationStage('idle');
        return;
      }
      
      // Get all unread notification IDs
      const unreadIds = notifications
        .filter(n => !n.read)
        .map(n => n.id);
      
      // Add all unread notifications to animation set
      setAnimatingNotifications(new Set(unreadIds));
      
      // Start the visual animation first with a small delay
      setTimeout(async () => {
        try {
          // Call the API endpoint to mark all notifications as read
          const response = await httpClient.request('/api/notifications/mark-all-read', {
            method: 'PUT'
          });
          
          if (!response.ok) {
            throw new Error(t('notifications.failedMarkAllRead', 'Failed to mark all notifications as read'));
          }
          
          // Update local state to mark all notifications as read
          setNotifications(prev => 
            prev.map(notif => ({ ...notif, read: true }))
          );
          
          // Wait for animation to complete before clearing
          setTimeout(() => {
            setAnimatingNotifications(new Set());
            setAnimationStage('done');
            
            // Reset animation stage after a moment
            setTimeout(() => {
              setAnimationStage('idle');
            }, 200);
          }, 1000);
        } catch (error) {
          console.error('Mark all as read API error:', error);
          setAnimatingNotifications(new Set());
          setAnimationStage('idle');
        }
      }, 100);
      
    } catch (err) {
      console.error('Mark all as read error:', err);
      setAnimatingNotifications(new Set());
      setAnimationStage('idle');
    } finally {
      setTimeout(() => {
        setMarkingAllAsRead(false);
      }, 1200);
    }
  };

  // =============================================
  // 🧪 TEST PUSH NOTIFICATION
  // =============================================
  const sendTestPushNotification = async () => {
    try {
      setSendingPushTest(true);
      setError(null);
      
      let currentToken = pushToken;
      
      // Check if we have a token
      if (!currentToken) {
        const storedToken = localStorage.getItem('pushToken');
        if (storedToken) {
          currentToken = storedToken;
          setPushToken(storedToken);
        } else {
          setError('No push token available. Please enable push notifications first.');
          setSendingPushTest(false);
          return;
        }
      }

      if (!httpClient) {
        setError('Not authenticated. Please log in again.');
        setSendingPushTest(false);
        return;
      }

      const response = await httpClient.request('/api/send-test-push-notification', {
        method: 'POST',
        body: JSON.stringify({
          push_token: currentToken,
          type: 'test',
          title: '🎉 EasySubs Test Notification',
          body: 'Push notifications are working correctly!',
          debug_info: {
            platform: Capacitor.getPlatform(),
            device_id: deviceInfo?.deviceId,
            timestamp: new Date().toISOString()
          }
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        setSaveSuccess('Test push notification sent! Check your device.');
        setTimeout(() => setSaveSuccess(false), 5000);
      } else {
        throw new Error(result.error || `Server error: ${response.status}`);
      }
    } catch (err) {
      setError('Failed to send push notification: ' + err.message);
    } finally {
      setSendingPushTest(false);
    }
  };

  // =============================================
  // 🔐 PERMISSION MANAGEMENT
  // =============================================
  const checkPushPermissionStatus = async () => {
    if (!hasPushSupport) {
      return 'not-supported';
    }
    
    try {
      const permStatus = await PushNotifications.checkPermissions();
      setPushPermissionStatus(permStatus.receive);
      return permStatus.receive;
    } catch (error) {
      return 'error';
    }
  };

  const requestNotificationPermission = async () => {
    if (!hasPushSupport) {
      return;
    }

    try {
      const permission = await PushNotifications.requestPermissions();
      
      if (permission.receive === 'granted') {
        setPushPermissionStatus('granted');
        await PushNotifications.register();
      } else {
        setPushPermissionStatus('denied');
        updateSettingsToDisablePush();
      }
    } catch (error) {
      setPushPermissionStatus('denied');
      updateSettingsToDisablePush();
    }
  };

  const updateSettingsToDisablePush = () => {
    const newSettings = { ...notificationSettings };
    Object.keys(newSettings.global_settings).forEach(key => {
      newSettings.global_settings[key].push = false;
    });
    setNotificationSettings(newSettings);
  };

  const handlePermissionDialog = async (userAccepted) => {
    setShowPermissionDialog(false);
    
    if (!userAccepted) {
      setPushPermissionStatus('denied');
      updateSettingsToDisablePush();
      return;
    }

    await requestNotificationPermission();
  };

  // =============================================
  // 📡 DATA FETCHING FUNCTIONS
  // =============================================
  const fetchNotificationSettings = async () => {
    try {
      setLoadingSettings(true);
      
      if (!httpClient) {
        setError(t('notifications.userIdNotFound', 'User not authenticated. Please log in again.'));
        setLoadingSettings(false);
        return;
      }
      
      const response = await httpClient.request('/api/notification-settings');
      
      if (!response.ok) {
        throw new Error(t('notifications.failedFetchSettings', 'Failed to fetch notification settings'));
      }

      const data = await response.json();
      setNotificationSettings(data);
    } catch (err) {
      console.error('Notification settings fetch error:', err);
    } finally {
      setLoadingSettings(false);
    }
  };

  const saveNotificationSettings = async () => {
    try {
      setSavingSettings(true);
      
      if (!httpClient) {
        setError(t('notifications.userIdNotFound', 'User not authenticated. Please log in again.'));
        setSavingSettings(false);
        return;
      }
      
      const response = await httpClient.request('/api/notification-settings', {
        method: 'PUT',
        body: JSON.stringify({
          global_settings: notificationSettings.global_settings,
          merchant_promo_settings: notificationSettings.merchant_promo_settings
        })
      });
      
      if (!response.ok) {
        throw new Error(t('notifications.failedUpdateSettings', 'Failed to update notification settings'));
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(t('notifications.failedUpdateSettings', 'Failed to update notification settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!httpClient) {
        setError(t('notifications.userIdNotFound', 'User not authenticated. Please log in again.'));
        setLoading(false);
        return;
      }
      
      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error(t('notifications.failedFetchNotifications', 'Failed to fetch notifications'));
      }

      const data = await response.json();
      
      const mappedNotifications = data.map(notif => {
        const isoDate = notif.notification_time;
        
        return {
          id: notif._id,
          type: notif.type,
          title: notif.title,
          message: notif.msg,
          date: formatRelativeDate(isoDate),
          read: notif.read_status,
          icon: getIconByType(notif.type),
          fullDate: formatFullDate(isoDate)
        };
      });
      
      setNotifications(mappedNotifications);
      
    } catch (err) {
      console.error('Notification fetch error:', err);
      
      // Handle token expiration
      if (err.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return fetchNotifications();
      }
      
      setError(t('notifications.failedLoadNotifications', 'Failed to load notifications. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  // =============================================
  // 🛠️ UTILITY FUNCTIONS
  // =============================================
  const formatRelativeDate = (isoDate) => {
    try {
      if (!isoDate) return t('notifications.now', 'Now');
      
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return t('notifications.now', 'Now');
      
      const now = new Date();
      const diffMs = now - date;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      
      if (diffMin < 5) return t('notifications.now', 'Now');
      if (diffMin < 60) return `${diffMin} ${t('notifications.minutesAgo', 'min ago')}`;
      
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr} ${t('notifications.hoursAgo', 'hr ago')}`;
      
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay === 1) return t('notifications.yesterday', 'Yesterday');
      return `${diffDay} ${t('notifications.daysAgo', 'days ago')}`;
    } catch (e) {
      console.error('Date error:', e);
      return t('notifications.now', 'Now');
    }
  };

  const formatFullDate = (isoDate) => {
    try {
      if (!isoDate) return '';
      
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Full date error:', e);
      return '';
    }
  };

  const getIconByType = (type) => {
    const icons = {
      payment: CreditCard,
      security: AlertCircle,
      updates: CheckCircle,
      promotions: Mail,
      sharesubs: Share2,
      freetrialalert: Calendar,
      priceradaralert: Bell,
      info: Bell
    };
    return icons[type?.toLowerCase()] || Bell;
  };

  // =============================================
  // 🎯 HANDLE NOTIFICATION CLICK
  // =============================================
  const handleNotificationClick = async (id) => {
    try {
      const notification = notifications.find(n => n.id === id);
      
      if (notification && !notification.read) {
        // Add to animation set
        setAnimatingNotifications(prev => new Set([...prev, id]));
        
        setTimeout(async () => {
          try {
            const response = await httpClient.request(`/api/notifications/${id}/read`, {
              method: 'PUT'
            });
            
            if (!response.ok) {
              throw new Error(t('notifications.failedMarkRead', 'Failed to update status'));
            }
            
            // Update local state
            setNotifications(prev => 
              prev.map(notif => 
                notif.id === id ? { ...notif, read: true } : notif
              )
            );
            
            // Remove from animation set after animation completes
            setTimeout(() => {
              setAnimatingNotifications(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
              });
            }, 800);
          } catch (error) {
            console.error('Mark as read API error:', error);
            setAnimatingNotifications(prev => {
              const newSet = new Set(prev);
              newSet.delete(id);
              return newSet;
            });
          }
        }, 20);
      }
      
      setExpandedId(expandedId === id ? null : id);
      
    } catch (err) {
      console.error('Mark as read error:', err);
      setError(t('notifications.failedMarkRead', 'Failed to mark as read'));
      setAnimatingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const toggleNotificationSetting = (category, channel) => {
    if (channel === 'push' && !notificationSettings.global_settings[category].push && 
        pushPermissionStatus !== 'granted' && hasPushSupport) {
      setShowPermissionDialog(true);
      return;
    }
    
    setNotificationSettings(prev => ({
      ...prev,
      global_settings: {
        ...prev.global_settings,
        [category]: {
          ...prev.global_settings[category],
          [channel]: !prev.global_settings[category][channel]
        }
      }
    }));
  };

  const checkFirstTimeVisit = () => {
    try {
      const hasVisitedNotifications = localStorage.getItem('hasVisitedNotifications');
      return !hasVisitedNotifications;
    } catch (err) {
      return false;
    }
  };

  // =============================================
  // 🚀 INITIAL SETUP EFFECT
  // =============================================
  useEffect(() => {
    const initialize = async () => {
      if (!user) return;
      
      await fetchNotifications();
      await fetchNotificationSettings();
      
      if (hasPushSupport) {
        const status = await checkPushPermissionStatus();
        
        const isFirstTime = checkFirstTimeVisit();
        
        if (isFirstTime && !permissionRequested && status === 'prompt') {
          setPermissionRequested(true);
          localStorage.setItem('hasVisitedNotifications', 'true');
          
          setTimeout(() => {
            setShowPermissionDialog(true);
          }, 2000);
        } else if (status === 'granted' && !pushToken) {
          setTimeout(async () => {
            try {
              await PushNotifications.register();
            } catch (err) {
              console.error('Auto-registration error:', err);
            }
          }, 1000);
        }
      }
    };

    initialize();
    
    const interval = setInterval(() => {
      // Refresh notifications periodically
      if (user) {
        fetchNotifications();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [permissionRequested, hasPushSupport, user]);

  // =============================================
  // 🎨 UI COMPONENTS - ADAPTED FOR MOBILE
  // =============================================
  const NotificationSettingToggle = ({ category, label, description }) => (
    <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-medium text-gray-900">{label}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
      
      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center gap-2">
          <MailIcon className="h-4 w-4 text-gray-500" />
          <span className="text-sm">{t('notifications.email', 'Email')}</span>
        </div>
        <button
          onClick={() => toggleNotificationSetting(category, 'email')}
          disabled={savingSettings}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#253985] focus:ring-offset-2 ${
            notificationSettings.global_settings[category]?.email 
              ? 'bg-[#253985]' 
              : 'bg-gray-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              notificationSettings.global_settings[category]?.email 
                ? 'translate-x-6' 
                : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      
      <div className="flex justify-between items-center mt-3">
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-gray-500" />
          <span className="text-sm">{t('notifications.inApp', 'In-App')}</span>
        </div>
        <button
          onClick={() => toggleNotificationSetting(category, 'inApp')}
          disabled={savingSettings}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#253985] focus:ring-offset-2 ${
            notificationSettings.global_settings[category]?.inApp 
              ? 'bg-[#253985]' 
              : 'bg-gray-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              notificationSettings.global_settings[category]?.inApp 
                ? 'translate-x-6' 
                : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      
      {hasPushSupport && (
        <div className="flex justify-between items-center mt-3">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-gray-500" />
            <span className="text-sm">{t('notifications.pushNotifications', 'Push Notifications')}</span>
          </div>
          <button
            onClick={() => toggleNotificationSetting(category, 'push')}
            disabled={savingSettings || pushPermissionStatus === 'denied'}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#253985] focus:ring-offset-2 ${
              notificationSettings.global_settings[category]?.push 
                ? 'bg-[#253985]' 
                : 'bg-gray-200'
            } ${pushPermissionStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                notificationSettings.global_settings[category]?.push 
                  ? 'translate-x-6' 
                  : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      )}
      
      {hasPushSupport && pushPermissionStatus === 'denied' && (
        <div className="mt-3 text-xs text-red-500">
          {t('notifications.pushDisabled', 'Push notifications are disabled in your device settings. Enable them to receive alerts.')}
        </div>
      )}
    </div>
  );

  // Simplified Push Notification Testing Section
  const PushTestingSection = () => (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 mt-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-blue-100 p-2 rounded-lg">
          <Zap className="h-6 w-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('notifications.pushTesting.title', 'Test Push Notifications')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t('notifications.pushTesting.description', 'Test if your push notifications are working correctly.')}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <button
          onClick={sendTestPushNotification}
          disabled={sendingPushTest || tokenRegistrationStatus !== 'registered' || !user}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {sendingPushTest ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('notifications.pushTesting.sendingTest', 'Sending Test...')}
            </>
          ) : (
            <>
              <Bell className="h-5 w-5" />
              {t('notifications.pushTesting.sendTest', 'Send Test Notification')}
            </>
          )}
        </button>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            {t('notifications.pushTesting.notReceiving', 'Not receiving notifications?')}{' '}
            <button
              onClick={() => setShowOSGuide(true)}
              className="text-blue-600 underline hover:text-blue-700 font-medium"
            >
              {currentOS === 'ios' 
                ? t('notifications.pushTesting.enableIOS', 'Enable notifications in iOS Settings')
                : t('notifications.pushTesting.enableAndroid', 'Enable notifications in Android Settings')
              }
            </button>
          </p>
        </div>
      </div>
    </div>
  );

  // Platform-specific OS Settings Guide
  const OSSettingsGuide = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {currentOS === 'ios' 
              ? t('notifications.osGuide.iosSettings', 'iOS Notification Settings')
              : t('notifications.osGuide.androidSettings', 'Android Notification Settings')
            }
          </h3>
          <button
            onClick={() => setShowOSGuide(false)}
            className="p-1 rounded-md hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {currentOS === 'ios' ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm font-bold">1</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('notifications.osGuide.iosStep1', 'Open Settings App')}</p>
                <p className="text-sm text-gray-600">{t('notifications.osGuide.iosStep1Desc', 'Tap the Settings app on your home screen')}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm font-bold">2</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('notifications.osGuide.iosStep2', 'Find EasySubs')}</p>
                <p className="text-sm text-gray-600">{t('notifications.osGuide.iosStep2Desc', 'Scroll down and tap on "EasySubs" in the app list')}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm font-bold">3</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('notifications.osGuide.iosStep3', 'Enable Notifications')}</p>
                <p className="text-sm text-gray-600">{t('notifications.osGuide.iosStep3Desc', 'Tap "Notifications" and turn on "Allow Notifications"')}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-sm font-bold">1</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('notifications.osGuide.androidStep1', 'Open Settings')}</p>
                <p className="text-sm text-gray-600">{t('notifications.osGuide.androidStep1Desc', 'Tap the Settings app on your device')}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-sm font-bold">2</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('notifications.osGuide.androidStep2', 'Go to Apps & Notifications')}</p>
                <p className="text-sm text-gray-600">{t('notifications.osGuide.androidStep2Desc', 'Tap "Apps" or "Apps & notifications" in settings')}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-sm font-bold">3</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('notifications.osGuide.androidStep3', 'Find EasySubs')}</p>
                <p className="text-sm text-gray-600">{t('notifications.osGuide.androidStep3Desc', 'Locate and tap on "EasySubs" in your app list')}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-sm font-bold">4</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('notifications.osGuide.androidStep4', 'Enable Notifications')}</p>
                <p className="text-sm text-gray-600">{t('notifications.osGuide.androidStep4Desc', 'Tap "Notifications" and turn on "Show notifications"')}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">{t('notifications.osGuide.disableInstructions', 'To Disable Notifications:')}</h4>
          <p className="text-sm text-gray-600">
            {t('notifications.osGuide.disableDescription', 'Follow the same steps above and turn off notifications in your device settings.')}
          </p>
        </div>

        <button
          onClick={() => setShowOSGuide(false)}
          className="w-full mt-4 px-4 py-2 bg-[#253985] text-white rounded-lg hover:bg-[#1e2e6e]"
        >
          {t('notifications.osGuide.gotIt', 'Got it')}
        </button>
      </div>
    </div>
  );

  const filteredNotifications = activeTab === 'all' 
    ? notifications 
    : notifications.filter(n => n.type === activeTab);

  const navItems = [
    { icon: Home, label: t('bottomNav.home', 'Home'), path: "/mobile-dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications', 'Notifications'),
      path: "/mobile-notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace', 'Marketplace'), path: "/mobile-marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs', 'Share Subs'), path: "/mobile-share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank', 'Connect Bank'), path: "/mobile-connectBank" },
    { icon: Users, label: t('bottomNav.partners', 'Partners'), path: "/mobile-partners" },
    { icon: Settings, label: t('bottomNav.settings', 'Settings'), path: "/mobile-account-settings" },
  ];

  // =============================================
  // 🎨 ANIMATION STYLES
  // =============================================
  const animationStyles = `
    @keyframes smoothFadeOutBlue {
      0% {
        background-color: rgba(219, 234, 254, 1);
        border-color: rgba(37, 57, 133, 1);
        transform: translateY(0);
      }
      50% {
        background-color: rgba(219, 234, 254, 0.6);
        border-color: rgba(37, 57, 133, 0.6);
        transform: translateY(-1px);
      }
      80% {
        background-color: rgba(219, 234, 254, 0.2);
        border-color: rgba(37, 57, 133, 0.2);
        transform: translateY(0);
      }
      100% {
        background-color: rgba(255, 255, 255, 1);
        border-color: rgba(229, 231, 235, 1);
        transform: translateY(0);
      }
    }
    
    @keyframes smoothFadeOutBadge {
      0% {
        opacity: 1;
        transform: scale(1);
      }
      30% {
        opacity: 0.8;
        transform: scale(1.05);
      }
      70% {
        opacity: 0.4;
        transform: scale(0.95);
      }
      100% {
        opacity: 0;
        transform: scale(0.9);
      }
    }
    
    .smooth-fade-out-blue {
      animation: smoothFadeOutBlue 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    .smooth-fade-out-badge {
      animation: smoothFadeOutBadge 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    .notification-container {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .staggered-animation {
      animation: gentleAppear 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    @keyframes gentleAppear {
      from {
        opacity: 0.9;
        transform: translateY(1px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  // =============================================
  // 🎯 MAIN RENDER
  // =============================================
  if (!user) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 justify-center items-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <style>{animationStyles}</style>
      
      <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />

      {/* Permission Dialog */}
      {showPermissionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="text-center">
              <Bell className="h-12 w-12 text-[#253985] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('notifications.enablePush', 'Enable Push Notifications')}
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                {t('notifications.pushDescription', 'Get instant alerts about payment reminders, subscription updates, and important account information even when the app is closed.')}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => handlePermissionDialog(false)}
                  className="flex-1 px-4 py-2 text-gray-500 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                  {t('notifications.notNow', 'Not Now')}
                </button>
                <button
                  onClick={() => handlePermissionDialog(true)}
                  className="flex-1 px-4 py-2 bg-[#253985] text-white rounded-lg text-sm hover:bg-[#1e2e6e]"
                >
                  {t('notifications.enable', 'Enable')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OS Settings Guide */}
      {showOSGuide && <OSSettingsGuide />}

      {/* Success Message */}
      {saveSuccess && (
        <div className="fixed top-20 right-4 z-50 animate-fade-in">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              <span>{typeof saveSuccess === 'string' ? saveSuccess : t('notifications.settingsSaved', 'Notification settings saved successfully!')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mx-4 mt-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Marking all as read indicator */}
      {markingAllAsRead && (
        <div className="fixed top-20 right-4 z-50 animate-fade-in">
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t('notifications.markingAllRead', 'Marking all notifications as read...')}</span>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Header with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-4 rounded-lg transition-all duration-500`}>
            <h1 className="text-xl font-bold">{t('notifications.title', 'Notifications')}</h1>
            <p className="text-white/90 text-sm">{t('notifications.subtitle', 'Manage your alerts and preferences')}</p>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-2 text-sm font-medium ${activeTab === 'all' ? 'text-[#253985] border-b-2 border-[#253985]' : 'text-gray-500'}`}
            >
              {t('notifications.allNotifications', 'All Notifications')}
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 py-2 text-sm font-medium ${activeTab === 'settings' ? 'text-[#253985] border-b-2 border-[#253985]' : 'text-gray-500'}`}
            >
              {t('notifications.notificationSettings', 'Notification Settings')}
            </button>
          </div>

          {activeTab === 'settings' ? (
            <div>
              {/* Settings Header */}
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-[#253985]">
                  {t('notifications.preferences', 'Notification Preferences')}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={fetchNotificationSettings}
                    disabled={loadingSettings}
                    className="p-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    <RefreshCw className={`h-5 w-5 ${loadingSettings ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={saveNotificationSettings}
                    disabled={savingSettings || loadingSettings}
                    className="p-2 text-white bg-[#253985] rounded-md hover:bg-[#1e2e6e]"
                  >
                    {savingSettings ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Save className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                {t('notifications.preferencesDescription', 'Choose how you want to receive different types of notifications')}
              </p>

              {loadingSettings ? (
                <div className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#253985]" />
                  <p className="mt-2 text-gray-500 text-sm">{t('notifications.loadingSettings', 'Loading settings...')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <NotificationSettingToggle 
                    category="ShareSubs" 
                    label={t('notifications.shareSubsLabel', 'ShareSubs Notifications')}
                    description={t('notifications.shareSubsDescription', 'Get notified when someone shares a subscription with you')} 
                  />
                  <NotificationSettingToggle 
                    category="Security" 
                    label={t('notifications.securityLabel', 'Security Alerts')}
                    description={t('notifications.securityDescription', 'Important alerts about your account security')} 
                  />
                  <NotificationSettingToggle 
                    category="Promotion" 
                    label={t('notifications.promotionLabel', 'Promotional Offers')}
                    description={t('notifications.promotionDescription', 'Special deals and promotions from merchants')} 
                  />
                  <NotificationSettingToggle 
                    category="FreeTrialAlert" 
                    label={t('notifications.freeTrialLabel', 'Free Trial Alerts')}
                    description={t('notifications.freeTrialDescription', 'Reminders before your free trials end')} 
                  />
                  <NotificationSettingToggle 
                    category="PriceRadarAlert" 
                    label={t('notifications.priceRadarLabel', 'Price Radar Alerts')}
                    description={t('notifications.priceRadarDescription', 'Notifications when subscription prices change')} 
                  />
                </div>
              )}

              {/* Push Notification Testing Section */}
              {hasPushSupport && <PushTestingSection />}
            </div>
          ) : (
            <>
              {/* Notifications Header */}
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-[#253985]">
                  {t('notifications.yourNotifications', 'Your Notifications')}
                </h2>
                <div className="flex gap-2">
                  {unreadCount > 0 && !markingAllAsRead && (
                    <button
                      onClick={smoothAnimateMarkAllAsRead}
                      disabled={markingAllAsRead}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle className="h-4 w-4" />
                      {t('notifications.markAllRead', 'Mark All as Read')}
                    </button>
                  )}
                  <button
                    onClick={fetchNotifications}
                    disabled={loading}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    {t('common.refresh', 'Refresh')}
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#253985]" />
                  <p className="mt-2 text-gray-500 text-sm">{t('notifications.loadingNotifications', 'Loading notifications...')}</p>
                </div>
              ) : filteredNotifications.length > 0 ? (
                <div className="space-y-3">
                  {filteredNotifications.map((notification, index) => {
                    const isAnimating = animatingNotifications.has(notification.id);
                    const isUnread = !notification.read && !isAnimating;
                    const isRead = notification.read || isAnimating;
                    const animationDelay = markingAllAsRead ? `${index * 40}ms` : '0ms';
                    
                    return (
                      <div 
                        key={notification.id}
                        className={`
                          notification-container
                          p-3 border rounded-lg flex flex-col cursor-pointer
                          ${isAnimating ? 'smooth-fade-out-blue' : ''}
                          ${isUnread ? 'border-[#253985] bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}
                          ${isRead && !isAnimating && animationStage === 'done' ? 'staggered-animation' : ''}
                        `}
                        style={{ 
                          animationDelay: isAnimating ? animationDelay : '0ms'
                        }}
                        onClick={() => handleNotificationClick(notification.id)}
                      >
                        <div className="flex items-start gap-3">
                          <notification.icon className={`flex-shrink-0 mt-1 h-5 w-5 ${
                            notification.type === 'security' ? 'text-red-500' : 'text-[#253985]'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <h3 className="font-medium text-sm text-gray-900">{notification.title}</h3>
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                {notification.date}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {expandedId === notification.id 
                                ? notification.message 
                                : `${notification.message.substring(0, 80)}${notification.message.length > 80 ? '...' : ''}`
                              }
                            </p>
                          </div>
                        </div>
                        
                        {expandedId === notification.id && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex flex-col text-xs text-gray-500 space-y-1">
                              <div>
                                <span className="font-medium">{t('notifications.type', 'Type')}:</span> 
                                <span className="capitalize ml-1">{notification.type}</span>
                              </div>
                              <div>
                                <span className="font-medium">{t('notifications.received', 'Received')}:</span> 
                                <span className="ml-1">{notification.fullDate}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {isUnread && expandedId !== notification.id && (
                          <div 
                            className={`mt-2 text-right ${isAnimating ? 'smooth-fade-out-badge' : ''}`}
                            style={{ animationDelay: isAnimating ? animationDelay : '0ms' }}
                          >
                            <span className="inline-block px-2 py-0.5 text-xs bg-[#253985] text-white rounded-full transition-all duration-300">
                              {t('notifications.new', 'New')}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <h3 className="text-gray-500 text-sm">{t('notifications.noNotifications', 'No notifications')}</h3>
                  <p className="text-gray-400 text-xs mt-1">{t('notifications.allCaughtUp', 'All caught up!')}</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <MobileBottomNav 
        navItems={navItems} 
        currentPath={location.pathname}
        maxVisibleItems={5}
        navigate={navigate}
        className="fixed bottom-0 w-full"
      />
    </div>
  );
};

export default MobileNotifications;